import type { ComputedRef, Ref } from 'vue';
import type { ResultItem } from '@/utils/file';
import type {
  InitialData as DashboardSearchFormInitialData,
  ValidData as DashboardSearchFormValidData,
} from '@/components/searchForms/DashboardSearchForm.vue';
import type { DataItem as DashboardTableDataItem } from '@/components/tables/DashboardTable.vue';
import type { Tabs } from '@/stores/useTabsStore';
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type { DashboardBadges } from '@/constants/tabs';

import { computed, ref, toRaw, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDebounceFn } from '@vueuse/core';
import { format } from 'date-fns-tz';
import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';

import { useStaticStore } from '@/stores/useStaticStore';
import { useUserStore } from '@/stores/useUserStore';
import * as locationQueryHelpers from '@/helpers/locationQuery';
import * as dashboardTableHelpers from '@/helpers/tables/dashboard';
import * as groupTrackingJobHelpers from '@/helpers/trackingJob/groupTrackingJob';
import * as routeNames from '@/constants/routeNames';

export interface PrimaryTrackingDashboardViewComposable {
  search: (formData: DashboardSearchFormValidData) => Promise<void>;
  initialData: ComputedRef<DashboardSearchFormInitialData>;
  total: Ref<number>;
  results: Ref<DashboardTableDataItem[]>;
  resultsForCsv: Ref<ResultItem[]>;
  driversItems: Ref<number[]>;
  tabsBadges: Ref<DashboardBadges>;
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  dialogData: Ref<{}>;
}

export default function usePrimaryTrackingDashboardView(
  queryDate: Readonly<Ref<Date | undefined>>,
  queryStates: Readonly<Ref<string[] | undefined>>,
  querySearch: Readonly<Ref<string | undefined>>,
  queryDrivers: Readonly<Ref<number[] | undefined>>,
  queryStatuses: Readonly<Ref<string[] | undefined>>,
  queryAttributes: Readonly<Ref<string[] | undefined>>,
  openDialog: () => void,
  isStaff: Readonly<Ref<boolean>>,
  dashboardTab: Ref<Tabs['dashboard']>,
  dashboardJobsData: Ref<
    openapi.DashboardJobsTrackingContentOpenapi | undefined
  >,
  isFetchingDashboardJobs: Readonly<Ref<boolean>>,
): PrimaryTrackingDashboardViewComposable {
  const router = useRouter();
  const route = useRoute();
  const userStore = useUserStore();
  const staticStore = useStaticStore();

  const userTimeZone = computed(() => userStore.user.timezone);

  const initialData = computed<DashboardSearchFormInitialData>(() => {
    let date;
    if (queryDate.value) {
      date = format(queryDate.value, 'yyyy-MM-dd');
    }
    return {
      date,
      states: queryStates.value,
      search: querySearch.value,
      drivers: queryDrivers.value,
      statuses: queryStatuses.value,
      attributes: queryAttributes.value,
    };
  });

  const results = ref<DashboardTableDataItem[]>([]);
  const filteredJobs = ref<openapi.DashboardJobsTrackingContentOpenapi>([]);
  const driversItems = ref<number[]>([]);
  const tabsBadges = ref<DashboardBadges>({});

  const total = computed(() => {
    return results.value.length;
  });

  const resultsForCsv = computed<ResultItem[]>(() => {
    return results.value.map((item) => {
      const { fold, trafficlight, ...resultItem } = item;
      return resultItem;
    });
  });

  function _search(formData: DashboardSearchFormValidData): void {
    // TODO when history back to empty query, formData will set query.date to the previousa url
    const query = locationQueryHelpers.buildDashboardSearchLocationQuery(
      route.query,
      formData,
    );
    const isReplace = _isEqual(route.query, query);
    void router.push({
      name: routeNames.JOBS,
      query,
      force: true,
      replace: isReplace,
    });
  }

  const debouncedSearch = useDebounceFn(_search, 500);

  async function search(formData: DashboardSearchFormValidData): Promise<void> {
    if (isForceUpdate.value) {
      _search(formData);
    } else {
      await debouncedSearch(formData);
    }
  }

  const isForceUpdate = ref(false);

  const dialogData = ref({});

  function updateResults(): void {
    if (!dashboardJobsData.value) {
      results.value = [];
      tabsBadges.value = {};
      return;
    }
    filteredJobs.value = _cloneDeep(toRaw(dashboardJobsData.value));
    driversItems.value = groupTrackingJobHelpers.getDriversItems(
      filteredJobs.value,
    );

    if (queryStates.value?.length) {
      filteredJobs.value = groupTrackingJobHelpers.filterJobsByStates(
        filteredJobs.value,
        queryStates.value,
      );
    }
    if (querySearch.value) {
      filteredJobs.value = groupTrackingJobHelpers.filterJobsBySearch(
        filteredJobs.value,
        querySearch.value,
      );
    }
    if (queryDrivers.value?.length) {
      filteredJobs.value = groupTrackingJobHelpers.filterJobsByDrivers(
        filteredJobs.value,
        queryDrivers.value,
      );
    }
    if (queryStatuses.value?.length) {
      filteredJobs.value = groupTrackingJobHelpers.filterJobsByStatuses(
        filteredJobs.value,
        queryStatuses.value,
      );
    }
    if (queryAttributes.value?.length) {
      filteredJobs.value = groupTrackingJobHelpers.filterJobsByAttributes(
        filteredJobs.value,
        queryAttributes.value,
      );
    }

    const categoryJobs = groupTrackingJobHelpers.getCategoryJobs(
      filteredJobs.value,
      userTimeZone.value,
    );

    tabsBadges.value = {
      all: categoryJobs.all.length,
      completed: categoryJobs.completed.length,
      inProgress: categoryJobs.inProgress.length,
      req4change: categoryJobs.req4change.length,
      unallocated: categoryJobs.unallocated.length,
      cancelled: categoryJobs.cancelled.length,
    };
    filteredJobs.value = categoryJobs[dashboardTab.value];

    filteredJobs.value = groupTrackingJobHelpers.getSortedJobs(
      filteredJobs.value,
    );

    results.value = filteredJobs.value.map((job) => {
      return dashboardTableHelpers.getTableItem(
        job,
        isStaff.value,
        dialogData,
        openDialog,
        categoryJobs,
        staticStore.getBases,
      );
    });
  }

  watch(isFetchingDashboardJobs, function (newValues, oldValues) {
    if (
      !(route.name === routeNames.JOBS || route.name === routeNames.JOBS_TV)
    ) {
      return;
    }
    if (isFetchingDashboardJobs.value) return;
    updateResults();
  });

  watch(
    [
      queryDate,
      queryStates,
      querySearch,
      queryDrivers,
      queryStatuses,
      queryAttributes,
    ],
    async (newValues, oldValues) => {
      if (
        !(route.name === routeNames.JOBS || route.name === routeNames.JOBS_TV)
      ) {
        return;
      }
      if (isFetchingDashboardJobs.value) return;
      if (isForceUpdate.value) {
        isForceUpdate.value = false;
        updateResults();
      } else {
        if (_isEqual(newValues, oldValues)) return;
        updateResults();
      }
    },
    { immediate: true },
  );

  watch(dashboardTab, function (newValues, oldValues) {
    if (
      !(route.name === routeNames.JOBS || route.name === routeNames.JOBS_TV)
    ) {
      return;
    }
    if (isFetchingDashboardJobs.value) return;
    updateResults();
  });

  return {
    search,
    initialData,
    total,
    results,
    resultsForCsv,
    driversItems,
    tabsBadges,
    dialogData,
  };
}
